import React, { useState, useEffect } from "react"
import { X } from "lucide-react"
import { Field, Input, Modal, VbButton } from "@/components/vb"
import DomainFavicon from "@/components/ui/DomainFavicon"
import { useCompanyUrl } from "@/hooks/useCompanyUrl"
import { FilterableMultiSelect } from "@/components/ui/filterable-multi-select"
import { validateEmail } from "@/lib/utils"
import { ScheduleConfig } from "@/services/ProjectsService"
import { useAuth } from "@/context/AuthContext.tsx"
import DayOfMonthGrid, {
    computeNextRun,
    describeGenerationDay,
    formatRunDate,
    normalizeGenerationDay,
} from "./DayOfMonthGrid"

/** Backend cap: ReportSchedule.recipients is an ArrayField(size=10). */
const MAX_RECIPIENTS = 10

interface ScheduleFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    mode: 'create' | 'edit'
    companyOptions: { value: number; label: string; url?: string | null }[]
    lockedCompany?: { value: number; label: string }
    scheduleToEdit?: ScheduleConfig | null
    onSave: (data: {
        companies?: string[]
        recipients: string[]
        generationDay: number
        scheduleId?: number
        companyId?: number
    }) => Promise<void>
}

export const ScheduleFormDialog = ({
    open,
    onOpenChange,
    mode,
    companyOptions,
    lockedCompany,
    scheduleToEdit,
    onSave
}: ScheduleFormDialogProps) => {
    const [selectedCompanies, setSelectedCompanies] = useState<string[]>([])
    const [email, setEmail] = useState("")
    const [emailList, setEmailList] = useState<string[]>([])
    const [generationDay, setGenerationDay] = useState<number>(
        normalizeGenerationDay(new Date().getDate()),
    )
    const { user } = useAuth();

    // Initialize form when editing. The API never returns `generation_day`
    // (write-only), so the day is back-derived from `next_generation_ts`.
    useEffect(() => {
        if (mode === 'edit' && scheduleToEdit && open) {
            setEmailList(scheduleToEdit.recipients)
            const nextGenDay = new Date(scheduleToEdit.next_generation_ts).getDate()
            setGenerationDay(normalizeGenerationDay(nextGenDay))
        }
    }, [mode, scheduleToEdit, open])

    useEffect(() => {
        if (open && mode === 'create' && lockedCompany) {
            setSelectedCompanies([String(lockedCompany.value)])
            setEmailList([user?.email ?? ''])
        }
    }, [open, mode, lockedCompany])

    const resetForm = () => {
        setEmail('')
        setEmailList([])
        setSelectedCompanies([])
        setGenerationDay(normalizeGenerationDay(new Date().getDate()))
    }

    const handleSave = async () => {
        const trimmedEmails = emailList.map(email => email.trim().toLowerCase());

        if (!trimmedEmails?.length) {
            return;
        }

        if (mode === 'create' && selectedCompanies.length === 0) {
            return;
        }

        await onSave({
            companies: mode === 'create' ? selectedCompanies : undefined,
            recipients: trimmedEmails,
            generationDay,
            scheduleId: mode === 'edit' ? scheduleToEdit?.id : undefined,
            companyId: mode === 'edit' ? scheduleToEdit?.company_id : undefined
        })

        onOpenChange(false)
        if (mode === 'create') {
            resetForm()
        }
    }

    const handleClose = () => {
        onOpenChange(false)
        if (mode === 'create') {
            resetForm()
        }
    }

    const addEmail = () => {
        const e = email.trim()
        if (e && validateEmail(e) && !emailList.includes(e) && emailList.length < MAX_RECIPIENTS) {
            setEmailList(all => [...all, e])
            setEmail('')
        }
    }

    const removeEmail = (e: string) => {
        setEmailList(all => all.filter(x => x !== e))
    }

    const recipientsFull = emailList.length >= MAX_RECIPIENTS
    const canAddEmail =
        !!email.trim() && validateEmail(email.trim()) && !recipientsFull
    const lockedLabel =
        mode === 'edit' ? scheduleToEdit?.company_name : lockedCompany?.label
    // The dialog only carries an id + name, so the favicon's host has to come
    // from the workspace's company list.
    const lockedCompanyId =
        mode === 'edit' ? scheduleToEdit?.company_id : lockedCompany?.value
    const companyUrl = useCompanyUrl()

    return (
        <Modal
            open={open}
            onOpenChange={(next) => {
                if (!next) {
                    handleClose()
                    return
                }
                onOpenChange(next)
            }}
            widthClassName="max-w-xl"
            // Restores the prevention the pre-AITS-494 dialog had: in create mode
            // the first tabbable control is the company combobox, which opens on
            // focus, so the dropdown would pop the instant the dialog does.
            onOpenAutoFocus={(e) => e.preventDefault()}
            title={mode === 'create' ? 'Set up automatic report delivery' : 'Edit scheduled report'}
            description={
                mode === 'create'
                    ? "Pick who gets the monthly report and which day of the month it runs."
                    : "Change who gets this report and which day of the month it runs."
            }
            footer={
                <>
                    <VbButton type="button" variant="secondary" onClick={handleClose}>
                        Cancel
                    </VbButton>
                    <VbButton
                        type="button"
                        onClick={() => void handleSave()}
                        disabled={
                            emailList.length === 0 ||
                            (mode === 'create' && !lockedCompany && selectedCompanies.length === 0)
                        }
                    >
                        Save
                    </VbButton>
                </>
            }
        >
            <div className="flex flex-col gap-5">
                {/* Company — a multi-select in create mode, otherwise the fixed
                    identity of the schedule being edited / the locked company. */}
                {mode === 'create' && !lockedCompany ? (
                    <Field
                        label="Companies"
                        hint="One schedule is created per company you pick."
                        group
                    >
                        <FilterableMultiSelect
                            options={companyOptions.map((opt) => ({
                                value: String(opt.value),
                                label: opt.label,
                                icon: (
                                    <DomainFavicon name={opt.label} url={opt.url} size={20} />
                                ),
                            }))}
                            value={selectedCompanies}
                            onChange={setSelectedCompanies}
                            placeholder="Search or add company..."
                            emptyMessage="No companies found"
                        />
                        {selectedCompanies.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {selectedCompanies.map(companyId => {
                                    const company = companyOptions.find(opt => String(opt.value) === companyId)
                                    return company ? (
                                        <span
                                            key={companyId}
                                            className="inline-flex items-center gap-1.5 rounded-vb-pill border border-vb-line bg-vb-bg-subtle py-1 pl-1.5 pr-1.5 text-[12.5px] font-semibold text-vb-text"
                                        >
                                            <DomainFavicon name={company.label} url={company.url} size={18} />
                                            {company.label}
                                            <button
                                                type="button"
                                                aria-label={`Remove ${company.label}`}
                                                onClick={() => setSelectedCompanies(prev => prev.filter(id => id !== companyId))}
                                                className="grid h-5 w-5 place-items-center rounded-vb-pill text-vb-text-3 transition-colors hover:bg-vb-surface hover:text-vb-text"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ) : null
                                })}
                            </div>
                        )}
                    </Field>
                ) : (
                    lockedLabel && (
                        <div className="flex items-center gap-3">
                            <DomainFavicon name={lockedLabel} url={companyUrl(lockedCompanyId)} size={40} />
                            <div className="min-w-0">
                                <div className="vb-dt-name">{lockedLabel}</div>
                                <div className="vb-dt-meta">Monthly report</div>
                            </div>
                        </div>
                    )
                )}

                {/* Recipients */}
                <Field
                    label="Recipients"
                    hint={`${emailList.length}/${MAX_RECIPIENTS} · they don't need an AITS account.`}
                    group
                    help={
                        recipientsFull
                            ? `Maximum ${MAX_RECIPIENTS} recipients reached.`
                            : undefined
                    }
                >
                    <div className="flex gap-2">
                        <Input
                            type="email"
                            placeholder="name@company.com"
                            value={email}
                            onChange={e => setEmail(e.currentTarget.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    addEmail()
                                }
                            }}
                            aria-label="Recipient email"
                            className="flex-1"
                            disabled={recipientsFull}
                        />
                        <VbButton type="button" onClick={addEmail} disabled={!canAddEmail}>
                            Add
                        </VbButton>
                    </div>

                    {emailList.length > 0 && (
                        <ul className="mt-2 flex max-h-40 flex-col gap-1 overflow-y-auto">
                            {emailList.map((e) => (
                                <li
                                    key={e}
                                    className="flex items-center gap-3 rounded-vb-sm border border-vb-line bg-vb-bg-subtle px-3 py-2"
                                >
                                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-vb-text-2">
                                        {e}
                                    </span>
                                    <button
                                        type="button"
                                        aria-label={`Remove ${e}`}
                                        onClick={() => removeEmail(e)}
                                        className="grid h-6 w-6 place-items-center rounded-vb-xs text-vb-text-3 transition-colors hover:bg-vb-surface hover:text-vb-fail-ink"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Field>

                {/* Day of the month — the same control the Reporting panel uses. */}
                <Field
                    label="Day of the month"
                    hint="Your monthly report runs on this day."
                    group
                    help={
                        <>
                            Selected: <strong>{describeGenerationDay(generationDay)}</strong> · Next
                            report: <strong>{formatRunDate(computeNextRun(generationDay))}</strong>
                        </>
                    }
                >
                    <DayOfMonthGrid value={generationDay} onChange={setGenerationDay} />
                </Field>
            </div>
        </Modal>
    )
}
